import { SelectQueryBuilder, sql } from 'kysely'
import { Database } from '../database'

export type OptimizationFilters = {
  createdBefore?: string
  createdAfter?: string
  updatedBefore?: string
  updatedAfter?: string
  deletedBefore?: string
  deletedAfter?: string
  tags?: string[]
  hidden?: boolean
  deprecated?: boolean
  type?: string[]
  application_ids?: string[]
  account_ids?: string[]
  search?: string
  hideExpenseless?: boolean
  licenses?: string[]
  suggestedLicenses?: string[]
}

export function applyOptimizationFilters<T>(
  query: SelectQueryBuilder<Database, 'optimization', T>,
  filters: OptimizationFilters
): SelectQueryBuilder<Database, 'optimization', T> {
  if (filters.tags?.length) {
    query = query.where('tags', '@>', JSON.stringify(filters.tags))
  }
  if (filters.createdBefore) {
    query = query.where('created', '<', filters.createdBefore)
  }
  if (filters.createdAfter) {
    query = query.where('created', '>', filters.createdAfter)
  }
  if (filters.updatedBefore) {
    query = query.where('updated', '<', filters.updatedBefore)
  }
  if (filters.updatedAfter) {
    query = query.where('updated', '>', filters.updatedAfter)
  }
  if (filters.deletedBefore) {
    query = query.where('deleted_at', '<', filters.deletedBefore)
  }
  if (filters.deletedAfter) {
    query = query.where('deleted_at', '>', filters.deletedAfter)
  }
  if (filters.hidden !== undefined) {
    query = query.where('hidden', '=', filters.hidden ? 1 : 0)
  }
  if (filters.deprecated !== undefined) {
    query = query.where('deprecated', '=', filters.deprecated ? 1 : 0)
  }
  if (filters.type?.length) {
    query = query.where('type', 'in', filters.type)
  }
  if (filters.application_ids && filters.application_ids.length > 0) {
    query = query.where((eb) =>
      eb.or([
        eb(
          sql`json_extract(${eb.ref('references')}, '$.software_id')`,
          'in',
          filters.application_ids
        ),
        eb(
          sql`json_extract(${eb.ref('references')}, '$.data_source_id')`,
          'in',
          filters.application_ids
        ),
      ])
    )
  }
  if (filters.account_ids && filters.account_ids.length > 0) {
    query = query.where((eb) =>
      eb(
        sql`json_extract(${eb.ref('references')}, '$.software_account_id')`,
        'in',
        filters.account_ids
      )
    )
  }
  if (filters.hideExpenseless) {
    query = query.where('savings', '>', 0)
  }
  if (filters.search) {
    query = query.where((eb) =>
      eb.or([
        eb('application_display_name', 'like', `%${filters.search}%`),
        eb('application_software_name', 'like', `%${filters.search}%`),
        eb('person_name', 'like', `%${filters.search}%`),
        eb('person_email', 'like', `%${filters.search}%`),
      ])
    )
  }

  if (filters.licenses?.length) {
    query = query.where((eb) =>
      eb.exists(
        eb
          .selectFrom(
            sql`json_each(${eb.ref('licenses')})`.as('license')
          )
          .select(sql`1`)
          .where('license.key', 'in', filters.licenses)
      )
    )
  }

  if (filters.suggestedLicenses?.length) {
    query = query.where((eb) =>
      eb.exists(
        eb
          .selectFrom(
            sql`json_each(json_extract(${eb.ref(
              'data'
            )}, '$.software_licenses_to_add'))`.as('item')
          )
          .select(sql`1`)
          .where(
            sql`json_extract(item.value, '$.name')`,
            'in',
            filters.suggestedLicenses
          )
      )
    )
  }

  return query
}
